import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Icon, Reveal, Overline, EASE } from "./primitives";
import { ROLES } from "../../data/content";

const domainColor = (d) => (d === "education" ? "#4F46E5" : "#0D9488");

export default function RoleExperience() {
  const [active, setActive] = useState(ROLES[0].key);
  const role = ROLES.find((r) => r.key === active);
  const color = domainColor(role.domain);

  return (
    <section className="relative bg-white py-20 sm:py-28" data-testid="section-roles">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-2xl">
            <Overline>Built for every role</Overline>
            <h2 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-ajmus-ink sm:text-5xl">
              See what matters to each person.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ajmus-steel sm:text-lg">
              Select a role to preview the kind of information and features that person sees first.
              Example content shown for illustration.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* role list */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-2">
              {ROLES.map((r) => {
                const on = r.key === active;
                const c = domainColor(r.domain);
                return (
                  <button
                    key={r.key}
                    onClick={() => setActive(r.key)}
                    data-testid={`role-btn-${r.key}`}
                    className="flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors duration-200"
                    style={{
                      borderColor: on ? c : "rgba(0,0,0,0.06)",
                      backgroundColor: on ? `${c}0d` : "#FAFAFA",
                    }}
                  >
                    <span
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: on ? c : "#fff", color: on ? "#fff" : c, border: on ? "none" : `1px solid ${c}22` }}
                    >
                      <Icon name={r.icon} size={17} />
                    </span>
                    <span className={`text-sm font-semibold ${on ? "text-ajmus-ink" : "text-ajmus-steel"}`}>
                      {r.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* preview panel */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="h-full rounded-3xl border border-black/5 bg-white p-8 shadow-[0_20px_60px_rgb(11,19,43,0.07)]"
                style={{ backgroundImage: `radial-gradient(120% 120% at 100% 0%, ${color}0d 0%, transparent 55%)` }}
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${color}14`, color }}>
                    <Icon name={role.icon} size={24} />
                  </span>
                  <div>
                    <h3 className="font-heading text-xl font-bold tracking-tight text-ajmus-ink">{role.label}</h3>
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color }}>
                      {role.domain === "both" ? "Cross-platform" : role.domain}
                    </span>
                  </div>
                </div>

                <div className="mt-6 space-y-2.5">
                  {role.items.map((it, i) => (
                    <motion.div
                      key={it.k}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, ease: EASE, delay: i * 0.06 }}
                      className="flex items-center justify-between rounded-xl border border-black/5 bg-[#FAFAFA] px-4 py-3.5"
                    >
                      <span className="flex items-center gap-2.5 text-sm font-semibold text-ajmus-ink">
                        <ChevronRight size={15} style={{ color }} />
                        {it.k}
                      </span>
                      <span className="text-xs font-medium text-ajmus-steel">{it.v}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
